import { extends as _extends } from '../../../_virtual/_rollupPluginBabelHelpers.js';
import React__default, { forwardRef, cloneElement } from 'react';
import PropTypes from 'prop-types';
import { StyledText, StyledOverline, StyledSubHeading, StyledHelpButtonContainer } from './Heading.style.js';
import { useHelpButton } from '../useHelpButton/use-help-button.js';
import { Row } from '../Grid/Row.js';
import { Col } from '../Grid/Col.js';

const headingFontSize = {
  1: {
    xs: 'xxxl',
    lg: 'xxxxxl'
  },
  2: {
    xs: 'xxxl',
    lg: 'xxxxl'
  },
  3: 'xxl',
  4: 'xl',
  5: 'lg',
  6: 'md'
};
const Heading = /*#__PURE__*/forwardRef((_ref, ref) => {
  let {
    align,
    as,
    children,
    className,
    help,
    helpButtonLabel,
    icon,
    id,
    inline,
    level = 1,
    noMargin,
    overline,
    size,
    subHeading,
    ...rest
  } = _ref;
  const {
    helpButton,
    helpContent
  } = useHelpButton({
    helpButtonLabel,
    helpText: help
  });
  const headingSize = size || level;
  const iconTShirtSize = ['xxxl', 'xxl', 'xl', 'lg', 'lg', 'md'];
  const heading = /*#__PURE__*/React__default.createElement(StyledText, _extends({}, rest, {
    $inline: inline,
    $noMargin: noMargin && !help,
    $withHelp: !!help,
    align: align,
    className: className,
    fontWeight: "semi-bold",
    forwardedAs: as || "h".concat(level),
    id: id,
    lineHeight: "xs",
    ref: ref,
    size: headingFontSize[headingSize]
  }), overline && /*#__PURE__*/React__default.createElement(StyledOverline, {
    $size: headingSize
  }, overline), icon ? /*#__PURE__*/React__default.createElement(Row, {
    alignItems: "start",
    gap: "xs"
  }, /*#__PURE__*/React__default.createElement(Col, {
    xs: "auto"
  }, /*#__PURE__*/cloneElement(icon, {
    block: true,
    color: 'primary',
    size: iconTShirtSize[headingSize - 1],
    // may be overridden by icon props
    ...icon.props
  })), /*#__PURE__*/React__default.createElement(Col, null, children)) : children, subHeading && /*#__PURE__*/React__default.createElement(StyledSubHeading, {
    $size: headingSize
  }, subHeading));
  return help ? /*#__PURE__*/React__default.createElement("div", {
    className: className
  }, heading, help && helpButtonLabel && /*#__PURE__*/React__default.createElement(React__default.Fragment, null, /*#__PURE__*/React__default.createElement(StyledHelpButtonContainer, null, helpButton), helpContent)) : heading;
});
const TEXT_ALIGN = ['center', 'right', 'left'];
Heading.displayName = 'Heading';
Heading.propTypes = {
  // @ts-expect-error
  align: PropTypes.oneOfType([PropTypes.oneOf(TEXT_ALIGN), PropTypes.shape({
    xs: PropTypes.oneOf(TEXT_ALIGN),
    sm: PropTypes.oneOf(TEXT_ALIGN),
    md: PropTypes.oneOf(TEXT_ALIGN),
    lg: PropTypes.oneOf(TEXT_ALIGN),
    xl: PropTypes.oneOf(TEXT_ALIGN)
  })]),
  /**
   * Element or component used for the root node.
   */
  as: PropTypes.elementType,
  /**
   * Text content of the component.
   */
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  help: PropTypes.node,
  helpButtonLabel: PropTypes.string,
  icon: PropTypes.element,
  iconSize: PropTypes.oneOf(['md', 'lg', 'xl', 'xxl', 'xxxl', 'layout-lg', 'layout-xl']),
  id: PropTypes.string,
  inline: PropTypes.bool,
  /**
   * Heading semantic level from 1 to 6.
   */
  level: PropTypes.oneOf([1, 2, 3, 4, 5, 6]),
  /**
   * Disable default bottom margin.
   */
  noMargin: PropTypes.bool,
  overline: PropTypes.string,
  /**
   * Heading size from 1 to 6. Same as level by default.
   */
  size: PropTypes.oneOf([1, 2, 3, 4, 5, 6]),
  subHeading: PropTypes.string
};

export { Heading };
